(ns storrito.editor.poll-v2-sticker
  (:require ["fabric" :refer [fabric]]
            [storrito.fabricjs.state :as f]
            [storrito.editor.centering :as centering]
            [clojure.string :as str]
            [storrito.editor.sticker-design :as sd]))

;; Concept:
;;
;; Creates a FabricJS object that looks like an Instagram poll
;; sticker.

(def poll-width 767)
(def option-height 141)
(def option-gap 21)

(defn get-textbox-lines
  "The lines of `text` as a FabricJS textbox with `params` wraps them.
   The textbox is created with the text: only then its width grows to
   the widest word that does not fit into `:width`. A textbox that is
   created empty keeps its width when `_splitTextIntoLines` is called
   afterwards, so the check in `fit-lines-textbox` never saw an
   over-long word.

   A missing `text` counts as an empty one: `(js/fabric.Textbox. nil)`
   throws `Cannot read properties of null (reading 'split')`, and a
   sticker that throws while it is prepared makes its design
   unrenderable (50 failed render attempts on 2026-09-29)."
  [text params]
  (let [textbox (js/fabric.Textbox. (or text "")
                                    (clj->js params))]
    [(vec (.-textLines textbox)) textbox]))

(defn fit-lines-textbox
  "Mimics the behavior of Instagram how text is wrapped.
   By default, they don't break words except the word
   is too long to fit in a single line.
   Or it is in a language like chinese/japanese
   that don't have a white space concept.
   Maybe we could try out this js solution that overrides the default _wrapLine of fabricjs:
   https://jjwilly.com/blog/fabricjs-2-0-breakwords"
  [text params max-lines]
  (let [join-lines (fn [lines]
                     (->> lines
                          (take max-lines)
                          (map str/trim)
                          (str/join "\n")))
        ;; The default textbox is not breaking any words,
        ;; but a long word makes the textbox wider than wanted
        [lines textbox] (get-textbox-lines text params)
        ;; ... then the fallback textbox breaks in-between words
        ;; (similar to Instagram). A text with too many lines is only
        ;; cut, packing it into `max-lines` by breaking words would
        ;; bring the mid-word breaks back.
        fitted-lines (if (<= (.-width textbox)
                             (:width params))
                       lines
                       (first (get-textbox-lines text (assoc params
                                                             :splitByGrapheme true))))
        shortened-text (join-lines fitted-lines)]
    (js/fabric.Textbox. shortened-text
                        (clj->js params))))

(defn option-group
  [{:keys [idx option top]}]
  (let [panel (js/fabric.Rect. #js{:width 675
                                   :rx 42
                                   :ry 42
                                   :height 141
                                   :originX "center"
                                   :originY "center"
                                   :fill "#efefef"})
        text (fit-lines-textbox (:text option)
                                {:fill "#000000"
                                 :breakWords true
                                 :originX "center"
                                 :originY "center"
                                 :width 580
                                 :left 0
                                 :fontSize 42
                                 :fontFamily "AvenyT"
                                 :textAlign "left"}
                                2)]
    (js/fabric.Group. (into-array [panel text])
                      #js{:originX "left"
                          :originY "top"
                          :top (+ top
                                  (* idx
                                     (+ option-gap option-height)))})))

(defn question-group
  [params]
  (let [question-text (fit-lines-textbox (str/upper-case (:question params))
                                         {:fill "#ffffff"
                                          :fontSize 64
                                          :lineHeight 1.01
                                          :originX "center"
                                          :originY "top"
                                          :width (- poll-width 80)
                                          :top 50
                                          :fontFamily "AvenyT"
                                          :fontWeight "bold"
                                          :textAlign "center"}
                                         3)
        question-panel-height (+ 100 (.-height question-text))
        question-panel-1 (js/fabric.Rect. #js{:width poll-width
                                              :height question-panel-height
                                              :rx 57
                                              :ry 57
                                              :originX "center"
                                              :originY "top"
                                              :fill (get sd/poll-v2-colors
                                                         (:color params))})
        question-panel-2 (js/fabric.Rect. #js{:width poll-width
                                              :height (/ question-panel-height 2)
                                              :top (/ question-panel-height 2)
                                              :originX "center"
                                              :originY "top"
                                              :fill (get sd/poll-v2-colors
                                                         (:color params))
                                              })]
    (js/fabric.Group. (into-array [question-panel-1 question-panel-2 question-text])
                      #js{:originX "left"
                          :originY "top"})))

(defn sticker
  "Builds the fabricjs representation of the new Instagram poll sticker"
  [{:keys [tallies color question] :as params}]
  ;; Bugfix for wrong text bounding boxes
  ;; Due to wrong calculated char width in cache, the cache needs to be cleared
  ;; Found at: https://github.com/kangax/fabric.js/issues/2612
  (js/fabric.util.clearFabricFontCache)
  (let [question-group (if-not (str/blank? question)
                         (question-group params)
                         (js/fabric.Group.))

        question-panel-height (.-height question-group)
        panel-object (js/fabric.Rect. #js{:fill "#ffffff"
                                          :width (- poll-width 2)
                                          :top 2            ;; To prohibit a visible edge of the background
                                          :rx 57
                                          :ry 57
                                          :height (+ question-panel-height
                                                     -2     ;; To prohibit a visible edge of the background
                                                     (* (inc (count tallies))
                                                        option-gap)
                                                     (* (count tallies)
                                                        option-height)
                                                     (* 2 23))
                                          :originX "center"
                                          :originY "top"})
        options-group (js/fabric.Group. (into-array
                                          (map-indexed
                                            (fn [idx option]
                                              (option-group {:idx idx
                                                             :option option
                                                             :top (+ 23 question-panel-height option-gap)}))
                                            tallies))
                                        #js{:originX "left"
                                            :originY "top"})]

    (js/fabric.Group. (into-array [panel-object question-group options-group])
                      #js{:id "poll-sticker-v2"
                          :lockScalingFlip true
                          :max-scale 1.8
                          :min-scale 0.3
                          :poll #js{:question question
                                    :tallies (clj->js tallies)
                                    :color color}
                          :top (or (:top params) 10)
                          :left (or (:left params) 10)
                          :angle (or (:angle params) 0.0)
                          :scaleY (or (:scaleX params) 1.0)
                          :scaleX (or (:scaleY params) 1.0)})))

(comment

  (let [sticker (sticker {:question "THIS IS A RATHER LONG QUESTION AND I HAD SOME MICRO SD CARDS"
                          :tallies [{:text "Yes"
                                     :font_size 42}
                                    {:text "No"
                                     :font_size 42}]
                          :color "green"})]
    (f/insert! (f/get-canvas) "poll-sticker-v2" sticker)
    (centering/center! sticker))

  (let [sticker (sticker {:question "WHOT DOES NOT LIKE ELEPHANTS?"
                          :tallies [{:text "If I must answer your questions I will be sure to a"
                                     :font_size 42}
                                    {:text "No"
                                     :font_size 42}
                                    {:text "Foo"
                                     :font_size 42}
                                    {:text "Why the hell not?"
                                     :font_size 42}]
                          :color "black"})]
    (f/insert! (f/get-canvas)
               "poll-sticker-v2"
               sticker)
    (centering/center! sticker))

  (let [sticker (sticker {:question "WHOT DOES NOT LIKE ELEPHANTS?"
                          :tallies [{:text "If I must answer your questions I will be sure to a"
                                     :font_size 42}
                                    {:text "No"
                                     :font_size 42}
                                    {:text "Foo"
                                     :font_size 42}]
                          :color "black"})]
    (f/insert! (f/get-canvas)
               "poll-sticker-v2"
               sticker)
    (centering/center! sticker))
  )
